import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { capitalCase } from "change-case";
import { EyeIcon, MessageCircle, TrashIcon } from "lucide-react";
import { useCallback, useState } from "react";

import { AddFeedButton } from "@/components/AddFeedButton";
import { AddPendingPostToCampaignDialog } from "@/components/AddPendingPostToCampaignDialog";
import { AdvertisedPostItem } from "@/components/AdvertisedPostItem";
import {
  Earned,
  Impressions,
  TotalBudget,
} from "@/components/CampaignAnalytics";
import { FeedOperatorCampaginFeedActionsMenu } from "@/components/FeedOperatorCampaginFeedActionsMenu";
import { FeedPageHeader } from "@/components/FeedPageHeader";
import { Page } from "@/components/Page";
import { RejectCampaignDialog } from "@/components/RejectCampaignDialog";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  PopoverContent,
  Popover,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { H1 } from "@/components/ui/typography";
import { UserAnalytics } from "@/components/UserAnalyticsSummary";
import {
  activateCampaign,
  campaignQueryOptions,
  feedCampaignQueryOptions,
  getPendingPostsForCampaignQueryOptions,
  PendingCampaignPost,
  proposeCampaign,
  removeStickyPostFromCampaign,
} from "@/lib/api";
import { Campaign, CampaignStatus, Feed, PublicFeed } from "@/lib/types";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/view/",
)({
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { campaignId } }) => {
    void Promise.all([
      queryClient.prefetchQuery(
        getPendingPostsForCampaignQueryOptions(parseInt(campaignId)),
      ),
      queryClient.prefetchQuery(campaignQueryOptions(parseInt(campaignId))),
      queryClient.prefetchQuery(
        feedCampaignQueryOptions({
          campaignId: [parseInt(campaignId)],
          days: 30,
        }),
      ),
    ]);
  },
});

function CampaignStats() {
  const { campaignId } = Route.useParams();
  const { data: feedCampaigns } = useSuspenseQuery(
    feedCampaignQueryOptions({ campaignId: [parseInt(campaignId)], days: 30 }),
  );

  return (
    <div className="flex flex-col md:flex-row gap-2 md:gap-4 md:h-[160px]">
      <UserAnalytics
        analytics={feedCampaigns.details}
        title="Total Views"
        dataKey="view_count"
        bigValue={feedCampaigns.summary.total_views}
      />
      <UserAnalytics
        analytics={feedCampaigns.details}
        title="Users"
        dataKey="unique_user_count"
        bigValue={feedCampaigns.summary.total_unique_user_views}
      />
    </div>
  );
}

function ActiveCell({
  feed,
  campaign,
}: {
  feed: Feed & { details: CampaignStatus };
  campaign: Campaign;
}) {
  const onActiveCampaignForFeed = useMutation({
    mutationFn: async (feedId: number) => {
      await activateCampaign(campaign.id, feedId);
    },
  });
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  if (feed.details.state === "proposed") {
    return (
      <Badge noIcon variant="warning">
        {capitalCase(feed.details.state)}
      </Badge>
    );
  }

  if (
    feed.details.state === "denied" ||
    feed.details.state === "rescinded" ||
    feed.details.state === "deactivated" ||
    feed.details.state === "cancelled"
  ) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Badge asChild noIcon variant="destructive">
            <button type="button">{capitalCase(feed.details.state)}</button>
          </Badge>
        </PopoverTrigger>
        <PopoverContent side="top">
          {feed.details.stopping_criteria?.reason ? (
            <div className="flex flex-col gap-2">
              <div className="font-semibold text-xs">Reason</div>
              <div>{feed.details.stopping_criteria.reason}</div>
            </div>
          ) : (
            "No reason provided"
          )}
        </PopoverContent>
      </Popover>
    );
  }

  if (feed.details.state === "active") {
    return (
      <RejectCampaignDialog
        type="deactivate"
        campaignId={campaign.id}
        feedId={feed.id}
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
      >
        <SimpleTooltip message="Deactivate">
          <div className="flex w-fit">
            <Switch
              aria-label={`${feed.display_name} active`}
              checked={true}
              size="sm"
              onCheckedChange={() => {
                setDeactivateDialogOpen(true);
              }}
            />
          </div>
        </SimpleTooltip>
      </RejectCampaignDialog>
    );
  }

  return (
    <SimpleTooltip message="Activate">
      <div className="flex w-fit">
        <Switch
          aria-label={`${feed.display_name} active`}
          checked={false}
          size="sm"
          onCheckedChange={() => {
            onActiveCampaignForFeed.mutate(feed.id);
          }}
        />
      </div>
    </SimpleTooltip>
  );
}

function PendingCampaignPostRow({
  post: { post, id: stickyPostId },
  campaignId,
}: {
  post: PendingCampaignPost;
  campaignId: number;
}) {
  const removeStickyPostFromCampaignMutation = useMutation({
    mutationFn: async (stickyPostId: number) => {
      await removeStickyPostFromCampaign(campaignId, stickyPostId);
    },
  });

  return (
    <div
      key={post.uri}
      className={cn(
        "border-b mb-4 pb-4 border-slate-6 dark:border-slatedark-6 last:border-b-0 last:mb-0 last:pb-0",
      )}
    >
      <div className="flex gap-4 items-center overflow-hidden">
        <Avatar size="sm" src={post.author.avatar} alt={post.author.handle} />
        <div className="flex flex-col gap-1 flex-1">
          <div className="text-xs text-slate-dim">@{post.author.handle}</div>
          <div className="line-clamp-2 whitespace-pre-wrap">
            {post.record.text}
          </div>
        </div>

        <Button
          variant="ghost"
          size="smIcon"
          disabled={removeStickyPostFromCampaignMutation.isPending}
          onClick={() => {
            removeStickyPostFromCampaignMutation.mutate(stickyPostId);
          }}
        >
          <span className="sr-only">Remove</span>
          <TrashIcon />
        </Button>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { campaignId } = Route.useParams();
  const { data: campaign } = useSuspenseQuery(
    campaignQueryOptions(parseInt(campaignId)),
  );
  const { data: pendingPosts } = useSuspenseQuery(
    getPendingPostsForCampaignQueryOptions(parseInt(campaignId)),
  );

  const onAddCampaign = useCallback(
    async (data: {
      feed: PublicFeed;
      cpm_cents: number;
      impressions_budget: number;
      reason: string | undefined;
    }) => {
      await proposeCampaign(campaign.id, data.feed.id, data);
    },
    [campaign.id],
  );

  return (
    <Page>
      <FeedPageHeader>
        <div className="flex items-center justify-between gap-8 self-stretch">
          <div className="flex gap-4 items-center self-stretch">
            <H1 className="text-2xl">{campaign.display_name}</H1>
          </div>
        </div>
      </FeedPageHeader>
      <div className="flex flex-col gap-8">
        <CampaignStats />
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-lg font-medium">Advertised Posts</div>
            <AddPendingPostToCampaignDialog campaignId={campaign.id} />
          </div>
          {campaign.sticky_posts.length > 0 || pendingPosts.length > 0 ? (
            <>
              {campaign.sticky_posts.map((stickyPost) => (
                <AdvertisedPostItem post={stickyPost} key={stickyPost.uri} />
              ))}

              {pendingPosts.length > 0 && (
                <>
                  <h3 className="text-sm font-medium">Pending Posts</h3>
                  <div
                    className={cn(
                      "bg-ambera-3 dark:bg-amberdarka-3",
                      "flex flex-col border-2 border-amber-6 dark:border-amberdark-6 rounded-lg p-4",
                    )}
                  >
                    {pendingPosts.map((post) => (
                      <PendingCampaignPostRow
                        key={post.id}
                        post={post}
                        campaignId={campaign.id}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div>No posts added.</div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-lg font-medium">Feeds</div>
            <AddFeedButton onAddCampaign={onAddCampaign} />
          </div>
          {campaign.algorithms.length > 0 ? (
            [...campaign.algorithms].reverse().map((feed) => {
              return (
                <div
                  key={`${feed.id}-campaign`}
                  className="group border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 flex flex-col gap-4"
                >
                  <div className="flex md:items-center justify-between gap-2">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-1 md:gap-2 flex-1 min-w-0">
                      <ActiveCell feed={feed} campaign={campaign} />
                      <div className="w-max font-medium text-lg truncate flex-1 min-w-0">
                        {feed.display_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {feed.details.state !== "proposed" && (
                        <>
                          <ButtonLink
                            variant="outline"
                            size="sm"
                            className="hidden md:flex"
                            to="/app/campaigns/$campaignId/view/$algoId/messages"
                            params={{
                              campaignId: campaign.id.toString(),
                              algoId: feed.id.toString(),
                            }}
                          >
                            Messages
                            {Boolean(feed.details.unread_message_count) && (
                              <Badge
                                variant="destructive"
                                className="pointer-events-none"
                              >
                                {feed.details.unread_message_count}
                              </Badge>
                            )}
                          </ButtonLink>
                          <ButtonLink
                            variant="outline"
                            size="smIcon"
                            className="md:hidden relative"
                            to="/app/campaigns/$campaignId/view/$algoId/messages"
                            params={{
                              campaignId: campaign.id.toString(),
                              algoId: feed.id.toString(),
                            }}
                          >
                            <span className="sr-only">Messages</span>
                            <MessageCircle />
                            {Boolean(feed.details.unread_message_count) && (
                              <div className="absolute top-1 right-1 w-2 h-2 bg-red-10 dark:bg-red-10 rounded-full" />
                            )}
                          </ButtonLink>
                        </>
                      )}
                      <ButtonLink
                        variant="outline"
                        size="sm"
                        className="hidden md:flex"
                        to="/app/campaigns/$campaignId/view/$algoId"
                        params={{
                          campaignId: campaign.id.toString(),
                          algoId: feed.id.toString(),
                        }}
                      >
                        View
                      </ButtonLink>
                      <ButtonLink
                        variant="outline"
                        size="smIcon"
                        className="md:hidden"
                        to="/app/campaigns/$campaignId/view/$algoId"
                        params={{
                          campaignId: campaign.id.toString(),
                          algoId: feed.id.toString(),
                        }}
                      >
                        <span className="sr-only">View</span>
                        <EyeIcon />
                      </ButtonLink>

                      <FeedOperatorCampaginFeedActionsMenu
                        feed={feed}
                        campaign={campaign}
                      >
                        <Button variant="ghost" size="smIcon">
                          <span className="sr-only">Feed actions</span>
            <DotsVerticalIcon />
                        </Button>
                      </FeedOperatorCampaginFeedActionsMenu>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                      <div className="flex-1 flex flex-col gap-2">
                        <Progress
                          value={feed.details.impressions_to_date ?? 0}
                          max={
                            feed.details.impressions_budget +
                            (feed.details.impressions_to_date ?? 0)
                          }
                        />
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div>Impressions</div>
                          <div>
                            <Impressions status={feed.details} />
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 flex flex-col gap-2">
                        <Progress
                          value={(feed.details.cpm_inflight ?? 0) / 100}
                          max={
                            ((feed.details.impressions_budget +
                              (feed.details.impressions_to_date ?? 0)) *
                              ((feed.details.cpm_cents || 0) / 100)) /
                            1000
                          }
                        />
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div>Spend</div>
                          <div>
                            <Earned status={feed.details} />{" "}
                            <span className="text-slate-dim">
                              {"/ "}
                              <TotalBudget status={feed.details} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-slate-dim">
                      {feed.details.days_to_completion
                        ? feed.details.days_to_completion > 14
                          ? "More than 2 weeks remaining"
                          : `${Intl.NumberFormat("en-US", {
                              style: "decimal",
                              maximumFractionDigits: 2,
                            }).format(
                              feed.details.days_to_completion,
                            )} days remaining`
                        : "No duration set"}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div>No feeds added.</div>
          )}
        </div>
      </div>
    </Page>
  );
}
